import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import BRAND from "../brand";
import {
  ArrowLeft, Clock, Target, Play, ChevronRight,
  Calendar, Filter, Globe, Mic
} from "lucide-react";

function getStatusLabel(status) {
  if (status === 'evaluated') return 'Evaluated';
  if (status === 'ended') return 'Ended';
  return 'Active';
}

export default function HistoryPage() {
  const { user } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    const fetchSessions = async () => {
      try {
        const { data } = await axios.get(`${API}/sessions`);
        setSessions(data);
      } catch (e) {
        console.error("[History] Failed to load sessions:", e);
      } finally {
        setLoading(false);
      }
    };
    fetchSessions();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const getStatusColor = (status) => {
    switch (status) {
      case "evaluated": return "bg-[#14B8A6]/10 text-[#14B8A6]";
      case "ended": return "bg-[#F59E0B]/10 text-[#F59E0B]";
      case "active": return "bg-[#2563EB]/10 text-[#2563EB]";
      default: return "bg-slate-100 text-slate-600";
    }
  };

  const getScoreColor = (score) => {
    if (!score) return "text-slate-300";
    if (score >= 80) return "text-[#14B8A6]";
    if (score >= 60) return "text-[#F59E0B]";
    return "text-red-500";
  };

  const getLanguageLabel = (lang) => {
    switch (lang) {
      case 'hinglish': return 'Hinglish';
      case 'hi': return 'Hindi';
      default: return 'English';
    }
  };

  const filteredSessions = sessions.filter(s => {
    if (filter === "all") return true;
    return s.status === filter;
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', { 
      day: 'numeric', 
      month: 'short', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-4 border-slate-200 border-t-[#2563EB] rounded-full animate-spin"></div>
          <p className="text-slate-500">Loading history...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-slate-200/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <Link to="/dashboard">
                <Button variant="ghost" size="icon" className="text-slate-500 hover:text-[#0B1F3B]">
                  <ArrowLeft className="w-5 h-5" />
                </Button>
              </Link>
              <img 
                src={BRAND.logoUrl} 
                alt="Revyniqx" 
                className="h-7 w-auto"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
              <div>
                <h1 className="text-lg font-bold text-[#0B1F3B]">Session History</h1>
                <p className="text-xs text-slate-400">{BRAND.productName}</p>
              </div>
            </div>

            <Link to="/simulator">
              <Button className="bg-[#0B1F3B] hover:bg-[#0B1F3B]/90 btn-premium" data-testid="new-session-btn">
                <Play className="w-4 h-4 mr-2" /> New Session
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filter Bar */}
        <div className="flex items-center justify-between mb-6 fade-up">
          <div>
            <h2 className="text-2xl font-bold text-[#0B1F3B]">Your Practice Sessions</h2>
            <p className="text-slate-400 text-sm">{filteredSessions.length} sessions found</p>
          </div>
          
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-40 border-slate-200" data-testid="filter-select">
              <Filter className="w-4 h-4 mr-2 text-slate-400" />
              <SelectValue placeholder="Filter" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Sessions</SelectItem>
              <SelectItem value="evaluated">Evaluated</SelectItem>
              <SelectItem value="ended">Ended</SelectItem>
              <SelectItem value="active">Active</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Sessions List */}
        {filteredSessions.length > 0 ? (
          <div className="space-y-3">
            {filteredSessions.map((session, idx) => (
              <Link 
                key={session.id} 
                to={session.status === 'evaluated' ? `/evaluation/${session.id}` : `/simulator?resume=${session.id}`}
                className="block"
                data-testid={`session-${session.id}`}
              >
                <Card className={`premium-card fade-up`} style={{ animationDelay: `${idx * 0.05}s` }}>
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-base font-semibold text-[#0B1F3B]">{session.scenario_name}</h3>
                          <Badge className={getStatusColor(session.status)}>
                            {getStatusLabel(session.status)}
                          </Badge>
                          {session.selected_language && (
                            <Badge variant="outline" className="text-xs border-[#2563EB]/20 text-[#2563EB]">
                              <Globe className="w-3 h-3 mr-1" />
                              {getLanguageLabel(session.selected_language)}
                            </Badge>
                          )}
                        </div>
                        
                        <div className="flex items-center gap-4 text-sm text-slate-400">
                          <div className="flex items-center gap-1">
                            <Calendar className="w-3.5 h-3.5" />
                            {formatDate(session.started_at)}
                          </div>
                          {session.ended_at && (
                            <div className="flex items-center gap-1">
                              <Clock className="w-3.5 h-3.5" />
                              {Math.round((new Date(session.ended_at) - new Date(session.started_at)) / 60000)} min
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center gap-6">
                        {session.total_score != null && (
                          <div className="text-center">
                            <p className={`text-2xl font-bold ${getScoreColor(session.total_score)}`}>{session.total_score}%</p>
                            <p className="text-xs text-slate-400">Score</p>
                          </div>
                        )}
                        {session.readiness_score != null && (
                          <div className="text-center">
                            <p className={`text-2xl font-bold ${getScoreColor(session.readiness_score)}`}>{session.readiness_score}%</p>
                            <p className="text-xs text-slate-400">Readiness</p>
                          </div>
                        )}
                        <ChevronRight className="w-5 h-5 text-slate-300" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        ) : (
          <Card className="premium-card text-center py-16 fade-up">
            <CardContent>
              <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Mic className="w-8 h-8 text-slate-300" />
              </div>
              <h3 className="text-xl font-semibold text-[#0B1F3B] mb-2">No sessions yet</h3>
              <p className="text-slate-400 mb-6 text-sm">Start practicing to see your session history here</p>
              <Link to="/simulator">
                <Button className="bg-[#0B1F3B] hover:bg-[#0B1F3B]/90 btn-premium">
                  <Play className="w-4 h-4 mr-2" /> Start First Session
                </Button>
              </Link>
            </CardContent>
          </Card>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-200/60 bg-white mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <img 
                src={BRAND.logoUrl} 
                alt="Revyniqx" 
                className="h-6 w-auto opacity-60"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
              <span className="text-sm text-slate-400">{BRAND.productName}</span>
            </div>
            <div className="text-center sm:text-right">
              <p className="text-xs text-slate-400">{BRAND.poweredBy}</p>
              <p className="text-xs text-slate-300">{BRAND.companyAttribution}</p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
